import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { PatientsSharedDocumentUploadModalComponent } from 'src/app/ui/components/patients-shared-document-upload-modal/patients-shared-document-upload-modal.component';
import { SharedDocumentUploadModalComponent } from 'src/app/ui/components/shared-document-upload-modal/shared-document-upload-modal.component';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { PatientGroupUpdated } from '../../../../../../data/dietitian/domain/events/patient_group_events';
import { SurveyCreated } from '../../../../../../data/dietitian/domain/events/survey/survey_events';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { LoggerRepository } from '../../../../../../data/logger/repositories/logger_repository';
import {
  PatientSelectionActivated,
  PatientSelectionArchived,
} from '../../../../../../data/patient/domain/events/patient_selection_event';
import { Patient } from '../../../../../../data/patient/domain/patient';
import { ConfirmationModalComponent } from '../../../../../components/confirmation-modal/confirmation-modal.component';
import { SendQuestionModalComponent } from '../../../../../patient/routes/messages/components/send-question-modal/send-question-modal.component';
import { SendSurveyModalComponent } from '../../../../../patient/routes/messages/components/send-survey-modal/send-survey-modal.component';
import { AccountInformationsViewModel } from '../../../account/routes/account-informations/account-informations.facade';
import { QuestionsService, SortListType } from '../../questions.service';
import { AddEncouragementModalComponent } from './components/add-encouragement-modal/add-encouragement-modal.component';
import { AddPatientToGroupModalComponent } from './components/add-patient-to-group-modal/add-patient-to-group-modal.component';
import { RemovePatientFromGroupModalComponent } from './components/remove-patient-from-group-modal/remove-patient-from-group-modal.component';
import { PatientListService } from './patient-list.service';
import {
  PatientsListFacade,
  PatientsListViewModel,
} from './patients-list.facade';

export interface PatientCheckable {
  checked: boolean;
  entity: Patient;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-patients-list',
  templateUrl: './patients-list.component.html',
  styleUrls: ['./patients-list.component.scss'],
  providers: [PatientsListFacade],
  encapsulation: ViewEncapsulation.None,
})
export class PatientsListComponent implements OnDestroy {
  private accountStatusSubscription?: Subscription;
  private patientGroupEventsSubscription?: Subscription;
  private surveyEventSubscription?: Subscription;
  private selectedSortListTypeSubscription?: Subscription;
  private patientStatusSubscription?: Subscription;
  private selectedGroupSubscription?: Subscription;
  private patientSelectionEventSubscription?: Subscription;
  private indeterminateSubscription: Subscription;
  private accountStatus$: Observable<AccountInformationsViewModel>;
  private patientStatus$: Observable<PatientsListViewModel>;
  public currentSearchQuery: string | undefined;
  public SortListType = SortListType;
  public bsModalRef?: BsModalRef;
  public dietitian?: Dietitian;
  public patientsList: PatientCheckable[] = [];
  public totalResults = 0;
  public isLoading = false;
  public isAppendLoading = false;
  public patientPerformingAction?: string;
  public isOnError = false;
  public currentPage = 0;
  public static itemsPerPage = 30;
  public groups: PatientGroup[] | undefined;
  public checkedAll: boolean;
  public indeterminate: boolean;
  public actionsButtonHasPending = false;
  public algolia = false;

  constructor(
    private facade: PatientsListFacade,
    private router: Router,
    private modalService: BsModalService,
    private toastr: ToastrService,
    private loggerRepository: LoggerRepository,
    public questionsService: QuestionsService,
    public patientListService: PatientListService,
  ) {
    this.isLoading = false;

    this.accountStatus$ = this.facade.accountStatus$;
    this.patientStatus$ = this.facade.patientsStatus$;

    this.accountStatusSubscription = this.accountStatus$.subscribe((state) => {
      this.dietitian = state.dietitian;
      if (this.dietitian) {
        this.facade
          .getDietitianGroups(this.dietitian.id.toString())
          .then((groups) => (this.groups = groups));
      }
    });

    this.selectedSortListTypeSubscription =
      this.questionsService.selectedSortListTypeEmitter.subscribe((type) => {
        if (type === SortListType.Group) {
          this.checkedAll = true;
        } else {
          this.checkedAll = false;
        }
        this.loadPage(0);
      });

    this.patientGroupEventsSubscription =
      this.facade.patientGroupEvents$.subscribe((event) => {
        if (event instanceof PatientGroupUpdated) {
          this.loadPage(0);
        }
      });

    this.surveyEventSubscription = this.facade.surveyEvents$.subscribe(
      (event) => {
        if (event instanceof SurveyCreated) {
          this.toastr.success('Questionnaire envoyé.');
        }
      },
    );

    this.patientSelectionEventSubscription =
      this.facade.patientSelectionEvent$.subscribe((event) => {
        if (event instanceof PatientSelectionArchived) {
          this.toastr.success(
            event.items.length +
              ' patient' +
              (event.items.length > 1 ? 's' : '') +
              ' archivé' +
              (event.items.length > 1 ? 's' : '') +
              '. La modification peut apparaitre avec un léger délai !',
          );
        }
        if (event instanceof PatientSelectionActivated) {
          this.toastr.success(
            event.items.length +
              ' patient' +
              (event.items.length > 1 ? 's' : '') +
              ' activé' +
              (event.items.length > 1 ? 's' : '') +
              '. La modification peut apparaitre avec un léger délai !',
          );
        }
      });

    this.indeterminateSubscription =
      this.patientListService.indeterminate.subscribe(() => {
        this.checkedAll =
          this.patientsList.filter((p) => p.checked).length ===
          this.patientsList.length;
        this.indeterminate = this.indeterminateCheckAll(this.patientsList);
      });
  }

  getMomentDate(date: Date) {
    return moment(date);
  }

  ngOnDestroy(): void {
    this.accountStatusSubscription?.unsubscribe();
    this.patientStatusSubscription?.unsubscribe();
    this.selectedSortListTypeSubscription?.unsubscribe();
    this.selectedGroupSubscription?.unsubscribe();
    this.patientGroupEventsSubscription?.unsubscribe();
    this.surveyEventSubscription?.unsubscribe();
    this.indeterminateSubscription?.unsubscribe();
    this.patientSelectionEventSubscription?.unsubscribe();
  }

  goToPatient(id: string): void {
    this.router.navigate(['/patient', id]).then();
  }

  onSendEncouragement(patient?: Patient) {
    if (patient || this.checkedAll || this.indeterminate) {
      const initialState: ModalOptions = {
        class: 'modal-lg modal-dialog-centered',
        initialState: {
          target: patient
            ? patient
            : this.checkedAll && !this.currentSearchQuery
            ? this.questionsService.selectedGroup
            : this.patientsList.filter((p) => p.checked).map((p) => p.entity),
          dietitian: this.dietitian,
        },
      };
      this.bsModalRef = this.modalService.show(
        AddEncouragementModalComponent,
        initialState,
      );
    }
  }

  onSendMessage(patient?: Patient) {
    if (patient || this.checkedAll || this.indeterminate) {
      const target = patient
        ? patient
        : this.checkedAll && !this.currentSearchQuery
        ? this.questionsService.selectedGroup
        : this.patientsList.filter((p) => p.checked).map((p) => p.entity);
      const initialState: ModalOptions = {
        class: 'modal-lg modal-dialog-centered',
        initialState: {
          target,
          archived:
            this.questionsService.selectedSortListType ===
            SortListType.Archived,
        },
      };
      this.bsModalRef = this.modalService.show(
        SendQuestionModalComponent,
        initialState,
      );

      if (this.bsModalRef.content instanceof SendQuestionModalComponent) {
        const subscription = this.bsModalRef.content.send.subscribe(() => {
          if (!this.questionsService.selectedGroup && this.indeterminate) {
            this.patientsList
              .filter((p) => p.checked)
              .forEach((p) => (p.checked = false));
            this.checkedAll = false;
            this.indeterminate = false;
            this.patientListService.indeterminate.next();
          } else if (
            this.questionsService.selectedGroup &&
            this.indeterminate
          ) {
            this.patientsList
              .filter((p) => !p.checked)
              .forEach((p) => (p.checked = true));
            this.checkedAll = true;
            this.indeterminate = false;
            this.patientListService.indeterminate.next();
          } else if (!this.questionsService.selectedGroup && this.checkedAll) {
            this.patientsList
              .filter((p) => !p.checked)
              .forEach((p) => (p.checked = true));
            this.checkedAll = true;
            this.indeterminate = false;
            this.patientListService.indeterminate.next();
          }
          subscription?.unsubscribe();
        });
      }
    }
  }

  onAddPatientToGroup(patient: Patient) {
    const initialState: ModalOptions = {
      class: 'modal-lg modal-dialog-centered',
      initialState: {
        target: patient,
      },
    };
    this.bsModalRef = this.modalService.show(
      AddPatientToGroupModalComponent,
      initialState,
    );

    if (this.bsModalRef.content instanceof AddPatientToGroupModalComponent) {
      this.bsModalRef.content.validate.subscribe((response) => {
        const index = this.patientsList
          .map((p) => p.entity.id.toString())
          .indexOf(patient.id.toString());
        if (index >= 0) {
          if (this.patientsList[index].entity.groupId) {
            this.patientsList[index].entity.groupId = [
              ...(this.patientsList[index].entity.groupId as string[]),
              response.groupId,
            ];
          } else {
            this.patientsList[index].entity.groupId = [response.groupId];
          }
        }
      });
    }
  }

  onArchivePatient(id: string) {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: `Archiver ce patient ?`,
        subtitle:
          'Une fois archivé vous pourrez toujours accéder à la fiche de votre patient. Cependant il sera classé dans le groupe "patients archivés" et vous devrez sélectionner ce groupe spécifiquement si vous souhaitez consulter les questions ou les journaux de vos patients archivés.',
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(async () => {
        await this.archivePatient(id);
      });
    }
  }

  async archivePatient(patientId: string) {
    this.patientPerformingAction = patientId;
    await this.facade.archivePatient(patientId);
    this.patientPerformingAction = undefined;
    this.loadPage(0);
  }

  onActivatePatient(id: string) {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: `Activer ce patient ?`,
        subtitle: 'Voulez-vous activer ce patient ?',
      },
    };
    this.bsModalRef = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (this.bsModalRef.content instanceof ConfirmationModalComponent) {
      this.bsModalRef.content.validate.subscribe(async () => {
        await this.activatePatient(id);
      });
    }
  }

  async activatePatient(patientId: string) {
    this.patientPerformingAction = patientId;
    await this.facade.activePatient(patientId);
    this.patientPerformingAction = undefined;
    this.loadPage(0);
  }

  onSendDocument() {
    if (this.checkedAll || this.indeterminate) {
      if (!this.dietitian) return;

      const options: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          dietitian: this.dietitian,
          target:
            this.checkedAll && !this.currentSearchQuery
              ? this.questionsService.selectedGroup
              : this.patientsList.filter((p) => p.checked).map((p) => p.entity),
        },
        ignoreBackdropClick: true,
      };
      this.bsModalRef = this.modalService.show(
        PatientsSharedDocumentUploadModalComponent,
        options,
      );

      if (
        this.bsModalRef.content instanceof
        PatientsSharedDocumentUploadModalComponent
      ) {
        const subscription = this.bsModalRef.content.dismiss.subscribe(() => {
          if (
            this.questionsService.selectedSortListType !== SortListType.Archived
          ) {
            if (!this.questionsService.selectedGroup && this.indeterminate) {
              this.patientsList
                .filter((p) => p.checked)
                .forEach((p) => (p.checked = false));
              this.checkedAll = false;
              this.indeterminate = false;
              this.patientListService.indeterminate.next();
            } else if (
              this.questionsService.selectedGroup &&
              this.indeterminate
            ) {
              this.patientsList
                .filter((p) => !p.checked)
                .forEach((p) => (p.checked = true));
              this.checkedAll = true;
              this.indeterminate = false;
              this.patientListService.indeterminate.next();
            }
          }
          subscription?.unsubscribe();
        });
      }
    }
  }

  sendDocumentToPatient(patient: Patient) {
    if (!this.dietitian) return;

    const options: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        dietitian: this.dietitian,
        patient,
      },
      ignoreBackdropClick: true,
    };
    this.bsModalRef = this.modalService.show(
      SharedDocumentUploadModalComponent,
      options,
    );
  }

  loadPage(page: number) {
    if (!this.dietitian) {
      return;
    }
    if (page === 0) {
      this.isLoading = true;
      this.patientsList = [];
    } else {
      this.isAppendLoading = true;
    }
    this.isOnError = false;
    this.facade
      .searchDietitianPatients(
        this.dietitian.userId.id.toString(),
        this.currentSearchQuery ?? '',
        this.questionsService.selectedSortListType ?? SortListType.All,
        this.questionsService.selectedGroup?.id?.toString() ?? null,
        PatientsListComponent.itemsPerPage,
        page,
        this.dietitian.patientsDefaultSort ?? 'updatedAt',
        this.algolia,
      )
      .then((pageResult) => {
        this.totalResults = pageResult.totalResults;
        this.currentPage = pageResult.page;
        pageResult.results.forEach((value) => {
          if (
            this.patientsList.filter(
              (p) => p.entity.id.toString() === value.id.toString(),
            ).length === 0
          ) {
            this.patientsList.push({
              entity: value,
              checked: this.checkedAll,
            });
          }
        });
      })
      .catch((error: any) => {
        this.loggerRepository
          .error(
            'PatientsListComponent',
            'searchDietitianPatients',
            JSON.stringify(error),
          )
          .then()
          .catch();
        this.isOnError = true;
      })
      .finally(() => {
        this.isLoading = false;
        this.isAppendLoading = false;
        this.patientListService.indeterminate.next();
      });
  }

  updateSearchQuery(query?: string) {
    this.currentSearchQuery = query;
    this.loadPage(0);
  }

  onSendSurvey(patient?: Patient) {
    if (patient || this.checkedAll || this.indeterminate) {
      const target = patient
        ? patient
        : this.checkedAll && !this.currentSearchQuery
        ? this.questionsService.selectedGroup
        : this.patientsList.filter((p) => p.checked).map((p) => p.entity);
      const initialState: ModalOptions = {
        class: 'modal-lg modal-dialog-centered',
        initialState: { target },
      };
      this.bsModalRef = this.modalService.show(
        SendSurveyModalComponent,
        initialState,
      );
      if (this.bsModalRef.content instanceof SendSurveyModalComponent) {
        const subscription = this.bsModalRef.content.send.subscribe(() => {
          if (
            this.questionsService.selectedSortListType !== SortListType.Archived
          ) {
            if (!this.questionsService.selectedGroup && this.indeterminate) {
              this.patientsList
                .filter((p) => p.checked)
                .forEach((p) => (p.checked = false));
              this.checkedAll = false;
              this.indeterminate = false;
              this.patientListService.indeterminate.next();
            } else if (
              this.questionsService.selectedGroup &&
              this.indeterminate
            ) {
              this.patientsList
                .filter((p) => !p.checked)
                .forEach((p) => (p.checked = true));
              this.checkedAll = true;
              this.indeterminate = false;
              this.patientListService.indeterminate.next();
            }
          }
          subscription?.unsubscribe();
        });
      }
    }
  }

  getGroupsLabel(patient: Patient): string[] {
    const labels: string[] = [];
    if (patient.groupId && patient.groupId.length > 0 && this.groups) {
      this.groups
        .filter(
          (g) =>
            patient.groupId && patient.groupId.indexOf(g.id.toString()) >= 0,
        )
        .forEach((g) => labels.push(g.name));
    }
    return labels;
  }

  onScroll() {
    this.loadPage(this.currentPage + 1);
  }

  onCheckAll(value: boolean) {
    this.patientsList.forEach((p) => (p.checked = value));
    this.patientListService.indeterminate.next();
  }

  private indeterminateCheckAll(patientsList: PatientCheckable[]): boolean {
    return (
      patientsList.filter((p) => p.checked).length > 0 &&
      patientsList.filter((p) => !p.checked).length > 0
    );
  }

  async onArchivedAll() {
    if (this.indeterminate) {
      const selectedPatients = this.patientsList
        .filter((p) => p.checked)
        .map((p) => p.entity);

      const initialState: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          title: "Confirmation d'archivage groupé...",
          subtitle:
            'Une fois archivés vous pourrez toujours accéder aux fiches de vos patients. ' +
            'Cependant ils seront classés dans le groupe "patients archivés" et ' +
            'vous devrez sélectionner ce groupe spécifiquement si vous souhaitez ' +
            'consulter les questions ou les journaux de vos patients archivés. <br>' +
            '<strong>Veuillez confirmer que vous souhaitez archiver ' +
            selectedPatients.length +
            ' patient' +
            (selectedPatients.length > 1 ? 's' : '') +
            ' ?</strong>',
          validateLabel: 'Oui',
          cancelLabel: 'Non',
        },
      };
      const confirm = this.modalService.show(
        ConfirmationModalComponent,
        initialState,
      );
      if (confirm.content instanceof ConfirmationModalComponent) {
        const subscription = confirm.content.validate.subscribe(async () => {
          this.actionsButtonHasPending = true;
          await this.facade.archiveAllPatients(selectedPatients);
          for (const patientId of selectedPatients.map((p) =>
            p.id.toString(),
          )) {
            const index = this.patientsList
              .map((p) => p.entity)
              .map((p) => p.id.toString())
              .indexOf(patientId);
            this.patientsList.splice(index, 1);
          }
          this.indeterminate = false;
          this.checkedAll = false;
          this.actionsButtonHasPending = false;
          subscription?.unsubscribe();
        });
      }
    }
  }

  async onGroupedAll() {
    if (this.indeterminate) {
      this.actionsButtonHasPending = true;
      const initialState: ModalOptions = {
        class: 'modal-lg modal-dialog-centered',
        initialState: {
          target:
            this.checkedAll && !this.currentSearchQuery
              ? this.questionsService.selectedGroup
              : this.patientsList.filter((p) => p.checked).map((p) => p.entity),
        },
      };
      this.bsModalRef = this.modalService.show(
        AddPatientToGroupModalComponent,
        initialState,
      );

      if (this.bsModalRef.content instanceof AddPatientToGroupModalComponent) {
        this.bsModalRef.content.validate.subscribe((response) => {
          for (const patient of response.target) {
            if (patient.groupId) {
              if (patient.groupId.indexOf(response.groupId) === -1) {
                patient.groupId = [...patient.groupId, response.groupId];
              }
            } else {
              patient.groupId = [response.groupId];
            }
          }
          this.actionsButtonHasPending = false;
        });
        this.bsModalRef.content.cancel.subscribe(
          () => (this.actionsButtonHasPending = false),
        );
      }
    }
  }

  async onActivateAll() {
    if (this.indeterminate) {
      const selectedPatients = this.patientsList
        .filter((p) => p.checked)
        .map((p) => p.entity);

      const initialState: ModalOptions = {
        class: 'modal-md modal-dialog-centered',
        initialState: {
          title: "Confirmation d'activation groupé...",
          subtitle:
            'Veuillez confirmer que vous souhaitez activer ' +
            selectedPatients.length +
            ' patient' +
            (selectedPatients.length > 1 ? 's' : '') +
            ' ?',
          validateLabel: 'Oui',
          cancelLabel: 'Non',
        },
      };
      const confirm = this.modalService.show(
        ConfirmationModalComponent,
        initialState,
      );
      if (confirm.content instanceof ConfirmationModalComponent) {
        const subscription = confirm.content.validate.subscribe(async () => {
          this.actionsButtonHasPending = true;
          await this.facade.activeAllPatients(selectedPatients);
          for (const patientId of selectedPatients.map((p) =>
            p.id.toString(),
          )) {
            const index = this.patientsList
              .map((p) => p.entity)
              .map((p) => p.id.toString())
              .indexOf(patientId);
            this.patientsList.splice(index, 1);
          }
          this.indeterminate = false;
          this.checkedAll = false;
          this.actionsButtonHasPending = false;
          subscription?.unsubscribe();
        });
      }
    }
    // else if (this.checkedAll) {
    //   const initialState: ModalOptions = {
    //     class: 'modal-md modal-dialog-centered',
    //     initialState: {
    //       title: "Confirmation d'activation groupé...",
    //       subtitle:
    //         'Veuillez confirmer que vous souhaitez activer tous les patients ?',
    //       validateLabel: 'Oui',
    //       cancelLabel: 'Non',
    //     },
    //   };
    //   const confirm = this.modalService.show(
    //     ConfirmationModalComponent,
    //     initialState,
    //   );
    //   if (confirm.content instanceof ConfirmationModalComponent) {
    //     const subscription = confirm.content.validate.subscribe(async () => {
    //       if (this.dietitian) {
    //         this.actionsButtonHasPending = true;
    //         const patients = await this.facade.getAllArchivedPatients(
    //           this.dietitian.id.toString(),
    //         );
    //         await this.facade.activeAllPatients(patients);
    //         this.patientsList = [];
    //         this.indeterminate = false;
    //         this.checkedAll = false;
    //         this.actionsButtonHasPending = false;
    //         subscription?.unsubscribe();
    //       } else {
    //         this.toastr.error('Diététicien.ne indéfini.e !');
    //         this.actionsButtonHasPending = false;
    //       }
    //     });
    //   }
    // }
  }

  targetName() {
    if (this.checkedAll) {
      if (!this.currentSearchQuery) {
        return 'à tous';
      } else {
        if (this.patientsList) {
          if (this.patientsList.filter((p) => p.checked).length > 1) {
            return 'aux patients sélectionnés';
          } else {
            if (this.patientsList.filter((p) => p.checked).length > 0) {
              return (
                'à ' +
                this.patientsList.filter((p) => p.checked)[0].entity.firstName +
                ' ' +
                this.patientsList.filter((p) => p.checked)[0].entity.lastName
              );
            }
          }
        }
      }
    } else if (this.indeterminate) {
      if (this.patientsList) {
        if (this.patientsList.filter((p) => p.checked).length > 1) {
          return 'aux patients sélectionnés';
        } else {
          if (this.patientsList.filter((p) => p.checked).length > 0) {
            return (
              'à ' +
              this.patientsList.filter((p) => p.checked)[0].entity.firstName +
              ' ' +
              this.patientsList.filter((p) => p.checked)[0].entity.lastName
            );
          }
        }
      }
    } else {
      return ' - Sélectionner au moins un patient !';
    }
    return '...';
  }

  targetNameAdmin() {
    if (this.indeterminate) {
      if (this.patientsList.filter((p) => p.checked).length > 1) {
        return 'les patients sélectionnés';
      } else if (this.patientsList.filter((p) => p.checked).length === 1) {
        return (
          this.patientsList.filter((p) => p.checked)[0].entity.firstName +
          ' ' +
          this.patientsList.filter((p) => p.checked)[0].entity.lastName
        );
      } else {
        return '...';
      }
    } else {
      if (this.checkedAll) {
        if (this.currentSearchQuery) {
          if (this.patientsList.filter((p) => p.checked).length > 1) {
            return 'les patients sélectionnés';
          } else if (this.patientsList.filter((p) => p.checked).length === 1) {
            return (
              this.patientsList.filter((p) => p.checked)[0].entity.firstName +
              ' ' +
              this.patientsList.filter((p) => p.checked)[0].entity.lastName
            );
          } else {
            return '...';
          }
        } else {
          return 'tous les patients';
        }
      } else {
        return ' - Sélectionner au moins un patient !';
      }
    }
  }

  onRemovePatientToGroup(patient: Patient) {
    const initialState: ModalOptions = {
      class: 'modal-lg modal-dialog-centered',
      initialState: {
        target: patient,
      },
    };
    this.bsModalRef = this.modalService.show(
      RemovePatientFromGroupModalComponent,
      initialState,
    );
    if (
      this.bsModalRef.content instanceof RemovePatientFromGroupModalComponent
    ) {
      this.bsModalRef.content.validate.subscribe((response) => {
        const index = this.patientsList
          .map((p) => p.entity.id.toString())
          .indexOf(patient.id.toString());
        if (index >= 0) {
          if (this.patientsList[index].entity.groupId) {
            const groupIdx = this.patientsList[index].entity.groupId?.indexOf(
              response.groupId,
            );
            if (groupIdx) {
              this.patientsList[index].entity.groupId?.splice(groupIdx, 1);
            }
          }
        }
      });
    }
  }

  onUngroupedAll() {
    if (this.indeterminate) {
      this.actionsButtonHasPending = true;
      const initialState: ModalOptions = {
        class: 'modal-lg modal-dialog-centered',
        initialState: {
          target:
            this.checkedAll && !this.currentSearchQuery
              ? this.questionsService.selectedGroup
              : this.patientsList.filter((p) => p.checked).map((p) => p.entity),
        },
      };
      this.bsModalRef = this.modalService.show(
        RemovePatientFromGroupModalComponent,
        initialState,
      );

      if (
        this.bsModalRef.content instanceof RemovePatientFromGroupModalComponent
      ) {
        this.bsModalRef.content.validate.subscribe(() => {
          this.actionsButtonHasPending = false;
        });
        this.bsModalRef.content.cancel.subscribe(
          () => (this.actionsButtonHasPending = false),
        );
      }
    }
  }

  getSortText() {
    if (this.dietitian) {
      if (this.dietitian.patientsDefaultSort === 'createdAt') {
        return 'Date de création (desc.)';
      } else if (this.dietitian.patientsDefaultSort === 'lastActiveDate') {
        return 'Date de dernière activité (desc.)';
      }
    }
    return 'Date de dernière modification (desc.)';
  }

  async updatePatientsDefaultSort(
    value: 'createdAt' | 'updatedAt' | 'lastActiveDate',
  ): Promise<Dietitian | undefined> {
    this.isLoading = true;
    if (this.dietitian) {
      this.dietitian.patientsDefaultSort = value;
      this.dietitian = await this.facade.updateDietitian(this.dietitian);
      this.loadPage(0);
    }
    return Promise.resolve(undefined);
  }

  onAlgoliaChange() {
    this.loadPage(0);
  }
}
